import { useState } from "react";
import { SlideOver } from "@/components/ui/slide-over";
import { Button } from "@/components/ui/button";
import { Notify, toast } from "@/components/ui/notify";
import { getFriendlyErrorMessage } from "@/lib/api-client";
import { useApplySequence, useProductionOrders } from "../hooks";
import { useSequenceBatch } from "./hooks/useSequenceBatch";
import { SequenceObjectiveStep } from "./SequenceObjectiveStep";
import { SequenceProgress } from "./SequenceProgress";
import { SequenceRanking } from "./SequenceRanking";
import { SequenceOrdersTable } from "./SequenceOrdersTable";

interface Props {
  lineId: string;
  onClose: () => void;
}

// Drawer de sequenciamento: escolher objetivo → simular a fila → comparar
// candidatos → aplicar. Orquestrador puro (hook + composição); a lógica vive em
// hooks/useSequenceBatch e os passos em componentes irmãos.
export function SequenceDrawer({ lineId, onClose }: Props) {
  const sequence = useSequenceBatch(lineId);
  const apply = useApplySequence(lineId);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const queue = useProductionOrders({ lineId, status: "SCHEDULED", limit: 100 });

  const orders = queue.data?.items ?? [];
  const labelOf = (orderId: string) => {
    const order = orders.find((o) => o.id === orderId);
    return order?.code ?? orderId.slice(0, 8);
  };
  const selected = sequence.rows.find((r) => r.simulationId === selectedId) ?? null;
  const anchorOf = () => {
    const first = orders[0]?.plannedStartAt;
    return first ? new Date(first) : null;
  };

  const terminal = sequence.status === "COMPLETED" || sequence.status === "NO_GAIN";

  async function confirmApply() {
    if (!selected) return;
    const ok = await Notify.confirm({
      variant: "warning",
      title: "Aplicar esta sequência?",
      description: `As ${selected.orders.length} ordens agendadas serão reprogramadas nesta ordem, com as durações projetadas pela simulação.`,
      confirmLabel: "Aplicar sequência",
    });
    if (!ok) return;
    try {
      await apply.mutateAsync(selected.simulationId);
      toast.success("Fila reprogramada.");
      onClose();
    } catch (err) {
      // 409 esperado (fila mudou / não cabe no horizonte): mensagem amigável + CTA de re-simular.
      toast.error(getFriendlyErrorMessage(err));
    }
  }

  return (
    <SlideOver open onOpenChange={(open) => !open && onClose()} title="Simular fila" width={560}>
      <div className="space-y-4 p-4">
        {!sequence.batchId ? (
          <SequenceObjectiveStep
            queueSize={queue.data?.total ?? orders.length}
            creating={sequence.creating}
            error={sequence.createError}
            onStart={sequence.start}
          />
        ) : null}

        {sequence.batchId && !terminal ? (
          <SequenceProgress
            evaluated={sequence.evaluatedCount}
            total={sequence.candidateCount}
            status={sequence.status}
            errorMessage={sequence.errorMessage}
          />
        ) : null}

        {terminal ? (
          <>
            {sequence.status === "NO_GAIN" ? (
              <p className="rounded-md border bg-muted/30 p-3 text-sm">
                Nenhuma alternativa superou a fila atual neste objetivo — manter a ordem de hoje é a
                melhor decisão entre as três testadas.
              </p>
            ) : null}
            <SequenceRanking
              rows={sequence.rows}
              metricLabel={sequence.metricLabel}
              selectedId={selectedId}
              onSelect={setSelectedId}
            />
            {selected ? (
              <div className="space-y-2 rounded-md border p-3">
                <p className="text-sm font-medium">{selected.label}</p>
                <SequenceOrdersTable
                  orders={selected.orders}
                  labelOf={labelOf}
                  anchor={anchorOf()}
                />
                {!selected.isBaseline ? (
                  <Button
                    className="w-full"
                    disabled={!selected.completed || apply.isPending}
                    onClick={() => void confirmApply()}
                  >
                    {apply.isPending ? "Aplicando…" : "Aplicar esta sequência"}
                  </Button>
                ) : null}
                <p className="text-[10px] text-muted-foreground">
                  As janelas reagendadas usam a duração projetada pela simulação, não a planejada
                  originalmente.
                </p>
              </div>
            ) : null}
            <Button variant="outline" size="sm" onClick={sequence.reset}>
              Simular de novo
            </Button>
          </>
        ) : null}
      </div>
    </SlideOver>
  );
}
